"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Check } from "lucide-react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: (error: string) => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
          /* Fires when Cloudflare decides to show the "Verify you are human"
             checkbox. Until the person ticks it there is no token, and that is
             normal rather than a failure. */
          "before-interactive-callback"?: () => void;
          "after-interactive-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact";
          retry?: "auto" | "never";
          "retry-interval"?: number;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      isSupported: () => boolean;
    };
  }
}

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  /**
   * Called with true when Cloudflare puts up an interactive challenge, and
   * false once the person has dealt with it. Callers use this to avoid
   * reporting a timeout while the widget is simply waiting to be clicked.
   */
  onInteractiveChange?: (interactive: boolean) => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact";
  className?: string;
  /**
   * If true, widget will not auto-render on mount.
   * Use execute() method to trigger verification manually.
   */
  manual?: boolean;
  /**
   * Ref to expose execute() method for manual mode
   */
  executeRef?: React.RefObject<{ execute: () => void; reset: () => void } | null>;
}

/**
 * Cloudflare Turnstile component
 * Renders Turnstile challenge and calls onVerify with the token when solved
 */
export function Turnstile({
  siteKey,
  onVerify,
  onError,
  onExpire,
  onInteractiveChange,
  theme = "auto",
  size = "normal",
  className,
  manual = false,
  executeRef,
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const hasAutoVerifiedRef = useRef(false);
  const isExecutingRef = useRef(false);

  // CRITICAL: Use real Turnstile in production, but allow dev-bypass in development
  // Cloudflare Turnstile doesn't support localhost, so we bypass in dev
  const isProduction = process.env.NODE_ENV === "production";

  // In development, auto-verify immediately (only once) if not manual
  // CRITICAL: Never auto-verify in manual mode - only when execute() is called
  useEffect(() => {
    if (!isProduction && !manual && !hasAutoVerifiedRef.current) {
      // Auto-verify with a bypass token in development (only if not manual)
      // Cloudflare Turnstile doesn't support localhost
      hasAutoVerifiedRef.current = true;
      onVerify("dev-bypass-token");
    }
  }, [isProduction, manual, onVerify]);

  // Map Turnstile error codes to user-friendly messages (generic only)
  const getErrorMessage = (errorCode: string): string => {
    // Always return generic message - never expose specific error details
    return "Verification failed. Please try again.";
  };

  // Load Turnstile script (production only - Cloudflare doesn't support localhost)
  useEffect(() => {
    // Skip in development (Cloudflare doesn't support localhost)
    if (!isProduction) {
      return;
    }

    // Check if script is already loaded
    if (window.turnstile) {
      setIsLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
    );
    if (existingScript) {
      // Wait for it to load or check if already loaded
      if (window.turnstile) {
        setIsLoaded(true);
      } else {
        existingScript.addEventListener("load", () => setIsLoaded(true));
        existingScript.addEventListener("error", () => {
          setError("Failed to load Turnstile. Please refresh the page.");
          if (onError) {
            onError("Failed to load Turnstile script");
          }
        });
      }
      return;
    }

    // Create and load script
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsLoaded(true);
    };
    script.onerror = () => {
      setError("Failed to load Turnstile. Please refresh the page.");
      if (onError) {
        onError("Failed to load Turnstile script");
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts before it loads
      if (!window.turnstile && script.parentNode) {
        try {
          document.head.removeChild(script);
        } catch (err) {
          // Script may have already been removed, ignore
        }
      }
    };
  }, [onError, isProduction]);

  // Validate site key before rendering (production only)
  useEffect(() => {
    if (!isProduction) {
      return; // Skip validation in development
    }

    if (!siteKey || siteKey.trim() === "") {
      // Fail loudly in production if site key is missing
      console.error("[Turnstile] NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set in production!");
      const errorMsg = "Verification failed. Please try again.";
      setError(errorMsg);
      if (onError) {
        onError("110200"); // Invalid site key error code
      }
    }
  }, [siteKey, onError, isProduction]);

  // Stable callbacks for Turnstile API to prevent re-rendering
  const handleVerify = useCallback((token: string) => {
    setIsVerified(true);
    onVerify(token);
  }, [onVerify]);

  const handleError = useCallback((errorCode: string) => {
    // Always return generic message - never expose specific error details
    const errorMessage = "Verification failed. Please try again.";
    setError(errorMessage);
    if (onError) {
      onError(errorCode);
    }
  }, [onError]);

  const handleExpire = useCallback(() => {
    if (onExpire) {
      onExpire();
    }
  }, [onExpire]);

  /* Held in a ref so that passing an inline callback cannot re-run the render
     effect, which would tear down a challenge the person is mid-way through. */
  const onInteractiveChangeRef = useRef(onInteractiveChange);
  onInteractiveChangeRef.current = onInteractiveChange;

  const handleBeforeInteractive = useCallback(() => {
    onInteractiveChangeRef.current?.(true);
  }, []);

  const handleAfterInteractive = useCallback(() => {
    onInteractiveChangeRef.current?.(false);
  }, []);

  // Render Turnstile widget (production only, and only if not manual)
  // CRITICAL: In manual mode, NEVER auto-render - only render when execute() is called
  // On Android Capacitor, delay render slightly so WebView is fully ready (reduces iframe load issues)
  useEffect(() => {
    // Skip in development (Cloudflare doesn't support localhost)
    if (!isProduction) {
      return;
    }

    // CRITICAL: In manual mode, NEVER auto-render - widget only appears when execute() is called
    if (manual) {
      return;
    }

    // Don't render if site key is missing or invalid
    if (!siteKey || siteKey.trim() === "") {
      return;
    }

    // CRITICAL: Only render once - check if widget already exists
    if (!isLoaded || !window.turnstile || !containerRef.current || widgetIdRef.current !== null) {
      return;
    }

    const isCapacitor =
      typeof window !== "undefined" && /Capacitor/i.test(navigator.userAgent);
    const delayMs = isCapacitor ? 400 : 0;

    const doRender = () => {
      if (!containerRef.current || widgetIdRef.current !== null || !window.turnstile) return;
      try {
        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: handleVerify,
          "error-callback": handleError,
          "expired-callback": handleExpire,
          "before-interactive-callback": handleBeforeInteractive,
          "after-interactive-callback": handleAfterInteractive,
          theme: "light", // Always use light theme
          size,
          retry: "never", // Prevent auto-retry loops
        });
        widgetIdRef.current = widgetId;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to render Turnstile";
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    };

    const timeoutId = delayMs > 0 ? window.setTimeout(doRender, delayMs) : null;
    if (timeoutId === null) {
      doRender();
    }

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [isLoaded, siteKey, handleVerify, handleError, handleExpire, theme, size, isProduction, manual]);

  // Expose execute and reset methods for manual mode
  useEffect(() => {
    if (!executeRef) {
      return;
    }

    const executeFn = () => {
      const isAndroidCapacitor = typeof window !== "undefined" && /Android/i.test(navigator.userAgent) && /Capacitor/i.test(navigator.userAgent);
      
      // CRITICAL: Android debugging - log Turnstile execution
      if (isAndroidCapacitor) {
        console.log("[Turnstile] [Android Debug] ===== EXECUTE CALLED =====", {
          timestamp: new Date().toISOString(),
          currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
          isExecuting: isExecutingRef.current,
          hasWidget: widgetIdRef.current !== null,
          isLoaded,
          hasTurnstile: !!window.turnstile,
        });
      }
      
      // CRITICAL: Prevent multiple simultaneous executions
      if (isExecutingRef.current) {
        console.warn("[Turnstile] Already executing, ignoring duplicate call");
        return;
      }

      // If widget already exists, reset it instead of creating a new one
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          if (isAndroidCapacitor) {
            console.log("[Turnstile] [Android Debug] Resetting existing widget...");
          }
          window.turnstile.reset(widgetIdRef.current);
          setError(null);
          return;
        } catch (err) {
          if (isAndroidCapacitor) {
            console.error("[Turnstile] [Android Debug] Reset failed, removing widget:", err);
          }
          // If reset fails, remove and recreate
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (removeErr) {
            // Ignore
          }
          widgetIdRef.current = null;
        }
      }

      // In development, immediately return bypass token (Cloudflare doesn't support localhost)
      if (!isProduction) {
        if (isAndroidCapacitor) {
          console.log("[Turnstile] [Android Debug] Development mode - bypassing (Cloudflare doesn't support localhost)");
        }
        onVerify("dev-bypass-token");
        return;
      }

      if (!isLoaded || !window.turnstile || !containerRef.current || !siteKey || siteKey.trim() === "") {
        if (isAndroidCapacitor) {
          console.error("[Turnstile] [Android Debug] Turnstile not ready:", {
            isLoaded,
            hasTurnstile: !!window.turnstile,
            hasContainer: !!containerRef.current,
            hasSiteKey: !!siteKey,
          });
        }
        if (onError) {
          onError("Turnstile not ready");
        }
        return;
      }

      isExecutingRef.current = true;

      if (isAndroidCapacitor) {
        console.log("[Turnstile] [Android Debug] Rendering Turnstile widget...");
      }

      try {
        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            isExecutingRef.current = false;
            setIsVerified(true);
            if (isAndroidCapacitor) {
              console.log("[Turnstile] [Android Debug] ===== VERIFICATION SUCCESS =====", {
                hasToken: !!token,
                tokenLength: token?.length || 0,
                timestamp: new Date().toISOString(),
                currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
              });
            }
            onVerify(token);
          },
          "error-callback": (errorCode: string) => {
            isExecutingRef.current = false;
            if (isAndroidCapacitor) {
              console.error("[Turnstile] [Android Debug] ===== VERIFICATION ERROR =====", {
                errorCode,
                timestamp: new Date().toISOString(),
                currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
              });
            }
            const errorMessage = getErrorMessage(errorCode);
            setError(errorMessage);
            if (onError) {
              onError(errorCode);
            }
            // Clean up widget on error
            if (widgetIdRef.current !== null && window.turnstile) {
              try {
                window.turnstile.remove(widgetIdRef.current);
              } catch (err) {
                // Ignore cleanup errors
              }
              widgetIdRef.current = null;
            }
          },
          "expired-callback": () => {
            isExecutingRef.current = false;
            if (isAndroidCapacitor) {
              console.log("[Turnstile] [Android Debug] Verification expired");
            }
            if (onExpire) {
              onExpire();
            }
          },
          "before-interactive-callback": handleBeforeInteractive,
          "after-interactive-callback": handleAfterInteractive,
          theme: "light", // Always use light theme
          size,
          retry: "never", // Prevent auto-retry loops
        });

        widgetIdRef.current = widgetId;
        
        if (isAndroidCapacitor) {
          console.log("[Turnstile] [Android Debug] Widget rendered successfully:", {
            widgetId,
            timestamp: new Date().toISOString(),
          });
        }
      } catch (err) {
        isExecutingRef.current = false;
        const errorMessage = err instanceof Error ? err.message : "Failed to render Turnstile";
        if (isAndroidCapacitor) {
          console.error("[Turnstile] [Android Debug] Render error:", {
            error: errorMessage,
            timestamp: new Date().toISOString(),
            currentUrl: typeof window !== "undefined" ? window.location.href : "unknown",
          });
        }
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    };

    const resetFn = () => {
      // Reset executing flag first
      isExecutingRef.current = false;
      setIsVerified(false);
      
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
          setError(null);
        } catch (err) {
          console.error("Failed to reset Turnstile:", err);
          // If reset fails, try to remove the widget
          try {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = null;
          } catch (removeErr) {
            // Ignore
          }
        }
      } else {
        // If no widget, clear error state
        setError(null);
      }
    };

    // Update the ref
    if (executeRef && 'current' in executeRef) {
      (executeRef as React.MutableRefObject<{ execute: () => void; reset: () => void } | null>).current = {
        execute: executeFn,
        reset: resetFn,
      };
    }
  }, [executeRef, isLoaded, siteKey, onVerify, onError, onExpire, theme, size, isProduction]);

  // Reset widget (useful for retrying)
  const reset = useCallback(() => {
    setIsVerified(false);
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        setError(null);
      } catch (err) {
        console.error("Failed to reset Turnstile:", err);
      }
    }
  }, []);

  // When verified, keep the widget visible but don't show the success message
  // The widget will remain in place but without the green "Verification complete" text
  if (isVerified) {
    // Return empty div to keep the space but hide the success message
    return (
      <div className={`w-full ${className || ""}`} style={{ display: 'block', clear: 'both' }}>
        <div className="w-full" style={{ minHeight: '65px' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`w-full ${className || ""}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-red-600">{error}</span>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setIsVerified(false);
              if (containerRef.current) {
                widgetIdRef.current = null;
                // Force re-render by resetting state
                setIsLoaded(false);
                setTimeout(() => setIsLoaded(true), 100);
              }
            }}
            className="text-sm text-primary hover:underline whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // In development, keep widget visible but don't show success message (auto-verify happens in useEffect)
  if (!isProduction && !manual) {
    if (isVerified || hasAutoVerifiedRef.current) {
      // Return empty div to keep the space but hide the success message
      return (
        <div className={`w-full ${className || ""}`} style={{ display: 'block', clear: 'both' }}>
          <div className="w-full" style={{ minHeight: '65px' }} />
        </div>
      );
    }
    return null;
  }

  // In manual mode, always render container (even in dev) so execute() can work
  if (manual) {
    return (
      <div className={`w-full block ${className || ""}`}>
        <div ref={containerRef} className="flex justify-center w-full" />
      </div>
    );
  }

  // Don't render if site key is missing in production
  if (!siteKey || siteKey.trim() === "") {
    return (
      <div className={`w-full ${className || ""}`}>
        <div className="text-sm text-red-600">
          Verification failed. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className || ""}`} style={{ display: 'block', clear: 'both' }}>
      <div ref={containerRef} className="flex justify-center w-full min-h-[65px]" style={{ display: 'block' }} />
    </div>
  );
}

